import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Box, FileText, Users, MapPin, LogIn, Loader2, Trash2, Plus, RefreshCw, Send, Zap, Calendar, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLogin({ onLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/admin/login`, {}, { auth: { username: user, password: pass } });
      onLogin({ username: user, password: pass });
    } catch {
      setError("Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-sm w-full bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <img src="/storajini-logo.png" alt="Storajini" className="w-8 h-8" />
          <span className="text-lg font-bold">Admin Panel</span>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <input data-testid="admin-username" type="text" placeholder="Username" value={user} onChange={(e) => setUser(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm" />
          <input data-testid="admin-password" type="password" placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm" />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2">
            <LogIn className="w-4 h-4" /> Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

function CitiesWaveTab({ auth }) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(null);
  const [newCity, setNewCity] = useState({ name: "", slug: "", region: "", population: 0, description: "" });

  const fetchCities = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/cities`, { auth });
      setCities(r.data.cities);
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  const addCity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/cities`, newCity, { auth });
      setNewCity({ name: "", slug: "", region: "", population: 0, description: "" });
      fetchCities();
    } catch (err) {
      alert(err.response?.data?.detail || "Error adding city");
    }
  };

  const deleteCity = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    try {
      await axios.delete(`${API}/admin/cities/${slug}`, { auth });
      fetchCities();
    } catch { alert("Error deleting city"); }
  };

  const deployWave = async (waveNum) => {
    setDeploying(waveNum);
    try {
      await axios.post(`${API}/admin/wave`, { wave: waveNum }, { auth });
      await fetchCities();
      alert(`Wave ${waveNum} deployed successfully!`);
    } catch (err) {
      alert(err.response?.data?.detail || `Wave ${waveNum} deployment failed`);
    }
    setDeploying(null);
  };

  const activeCount = cities.filter(c => c.is_active).length;
  const waves = [
    { num: 1, count: 20, label: "Metro Manila Core" },
    { num: 2, count: 35, label: "+ Major Cities" },
    { num: 3, count: 50, label: "+ All Original Cities" },
    { num: 4, count: 100, label: "+ Regional Capitals" },
    { num: 5, count: 150, label: "+ All Cities & Towns" },
  ];

  return (
    <div>
      {/* Wave Deployment */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Wave Deployment</h3>
        <p className="text-sm text-gray-500 mb-4">Activate cities in waves. Each wave includes all previous wave cities.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {waves.map((wave) => (
            <div key={wave.num} className={`text-center p-5 rounded-xl border-2 ${activeCount >= wave.count ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
              <p className="text-sm font-semibold text-gray-600">Wave {wave.num}</p>
              <p className="text-3xl font-extrabold text-gray-900 my-2">{wave.count}</p>
              <p className="text-xs text-gray-500 mb-3">{wave.label}</p>
              {activeCount >= wave.count ? (
                <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-lg inline-block">Deployed</span>
              ) : (
                <button
                  data-testid={`deploy-wave-${wave.num}`}
                  onClick={() => deployWave(wave.num)}
                  disabled={deploying !== null}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {deploying === wave.num ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Deploy Wave {wave.num}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add City */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Add New City</h3>
        <form onSubmit={addCity} className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <input placeholder="City Name" value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") })} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" required />
          <input placeholder="Slug" value={newCity.slug} onChange={(e) => setNewCity({ ...newCity, slug: e.target.value })} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" required />
          <input placeholder="Region" value={newCity.region} onChange={(e) => setNewCity({ ...newCity, region: e.target.value })} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" required />
          <input placeholder="Population" type="number" value={newCity.population} onChange={(e) => setNewCity({ ...newCity, population: parseInt(e.target.value) || 0 })} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          <button type="submit" className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>
      </div>

      {/* Active Cities */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Active Cities ({activeCount} of {cities.length})</h3>
          <button onClick={fetchCities} className="text-amber-600 hover:text-amber-700"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {cities.filter(c => c.is_active).sort((a, b) => a.name.localeCompare(b.name)).map((city) => (
              <div key={city.slug} className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                <div>
                  <span className="text-sm font-medium text-gray-900">{city.name}</span>
                  <span className="text-xs text-gray-500 ml-1">{city.region}</span>
                </div>
                <button onClick={() => deleteCity(city.slug)} className="text-red-400 hover:text-red-600 ml-2"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BlogTab({ auth }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [genCategory, setGenCategory] = useState("storage");
  const [autoSchedule, setAutoSchedule] = useState(false);
  const [schedulingBatch, setSchedulingBatch] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/blog`, { auth });
      setPosts(r.data.posts);
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const generatePost = async (e) => {
    e.preventDefault();
    if (!genTopic.trim()) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/blog/generate`, { topic: genTopic, category: genCategory }, { auth });
      setGenTopic("");
      fetchPosts();
    } catch {
      alert("Error generating post");
    }
    setGenerating(false);
  };

  const deletePost = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${slug}`, { auth });
      fetchPosts();
    } catch { alert("Error deleting post"); }
  };

  const AUTO_TOPICS = [
    { topic: "Best Storage Solutions for Condo Living in the Philippines", category: "storage" },
    { topic: "How to Find Safe Parking in Metro Manila Business Districts", category: "parking" },
    { topic: "Moving from Province to Manila: A Complete Storage Guide", category: "moving" },
    { topic: "Decluttering Tips for Filipino Families Before a Move", category: "decluttering" },
    { topic: "Climate-Proof Storage: Protecting Your Items in Tropical Weather", category: "storage" },
    { topic: "Motorcycle Parking Guide: Best Options Across Philippine Cities", category: "parking" },
    { topic: "Warehouse Storage vs Spare Room Rental: Which is Right for You?", category: "storage" },
    { topic: "The Ultimate Guide to Peer-to-Peer Storage in the Philippines", category: "storage" },
  ];

  const generateBatch = async () => {
    if (!window.confirm("This will generate 8 blog posts using AI. Continue?")) return;
    setSchedulingBatch(true);
    const remainingTopics = AUTO_TOPICS.filter(t => !posts.some(p => p.title === t.topic));
    for (const topicData of remainingTopics.slice(0, 4)) {
      try {
        await axios.post(`${API}/admin/blog/generate`, topicData, { auth });
      } catch { /* continue */ }
    }
    await fetchPosts();
    setSchedulingBatch(false);
    alert("Batch generation complete!");
  };

  return (
    <div>
      {/* Auto Scheduler */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Automated Blog Scheduler</h3>
            <p className="text-sm text-gray-500 mt-1">Generate multiple posts at once with AI</p>
          </div>
          <Calendar className="w-6 h-6 text-amber-500" />
        </div>
        <div className="bg-amber-50 rounded-xl p-4 mb-4">
          <p className="text-sm text-amber-800 font-medium mb-2">Upcoming Auto-Topics:</p>
          <div className="space-y-1">
            {AUTO_TOPICS.slice(0, 4).map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  posts.some(p => p.title === t.topic) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {posts.some(p => p.title === t.topic) ? 'Published' : 'Pending'}
                </span>
                <span className="text-sm text-gray-700 truncate">{t.topic}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          data-testid="generate-batch-btn"
          onClick={generateBatch}
          disabled={schedulingBatch}
          className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {schedulingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {schedulingBatch ? "Generating..." : "Generate Batch (4 Posts)"}
        </button>
      </div>

      {/* Manual Generate */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Generate Single Post</h3>
        <form onSubmit={generatePost} className="flex flex-col sm:flex-row gap-3">
          <input placeholder="Blog topic (e.g., Best Storage Tips for Cebu)" value={genTopic} onChange={(e) => setGenTopic(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm" required />
          <select value={genCategory} onChange={(e) => setGenCategory(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm">
            <option value="storage">Storage</option>
            <option value="parking">Parking</option>
            <option value="moving">Moving</option>
            <option value="decluttering">Decluttering</option>
          </select>
          <button type="submit" disabled={generating} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-1 whitespace-nowrap">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {generating ? "Generating..." : "Generate with AI"}
          </button>
        </form>
      </div>

      {/* Posts List */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Blog Posts ({posts.length})</h3>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /> : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post.slug} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm truncate">{post.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full capitalize">{post.category}</span>
                    <span className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <button onClick={() => deletePost(post.slug)} className="ml-3 text-red-400 hover:text-red-600 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsTab({ auth }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/leads`, { auth });
      setLeads(r.data.leads);
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { auth });
      fetchLeads();
    } catch { alert("Error"); }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">Leads ({leads.length})</h3>
          <button onClick={fetchLeads} className="text-amber-600 hover:text-amber-700"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /> : leads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No leads captured yet.</p>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => (
              <div key={lead.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{lead.name}</p>
                    <p className="text-sm text-gray-500">{lead.email} {lead.phone && `| ${lead.phone}`}</p>
                    {lead.message && <p className="text-sm text-gray-400 mt-1">{lead.message}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      {lead.city && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{lead.city}</span>}
                      {lead.service_type && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">{lead.service_type}</span>}
                      <span className="text-xs text-gray-400">{new Date(lead.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <button onClick={() => deleteLead(lead.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IndexNowTab({ auth }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    // Calculate total pages
    axios.get(`${API}/admin/cities`, { auth }).then(r => {
      const cityCount = r.data.cities.filter(c => c.is_active).length;
      // Each city has 3 page types + homepage + blog listing + about + compare + privacy + cookie = 6 static pages
      const blogCount = 4; // approximate
      setTotalPages(cityCount * 3 + 6 + blogCount);
    }).catch(() => {});
  }, [auth]);

  const submitAll = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/indexnow`, {}, { auth });
      setResult(res.data);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || "Submission failed" });
    }
    setSubmitting(false);
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <Send className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">IndexNow - Search Engine Submission</h3>
            <p className="text-sm text-gray-500">Instantly notify Bing, Yahoo, Yandex, and DuckDuckGo about your pages</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          IndexNow instantly notifies <strong>Bing, Yahoo, Yandex, and DuckDuckGo</strong> about your pages so they get indexed faster. 
          Google uses its own crawling system (submit your sitemap via <a href="https://search.google.com/search-console" className="text-amber-600 underline">Google Search Console</a>).
        </p>

        <div className="flex items-center justify-center gap-8 py-6 mb-6 bg-gray-50 rounded-xl">
          <div className="text-center">
            <p className="text-4xl font-extrabold text-amber-600">{totalPages}</p>
            <p className="text-sm text-gray-500">Total Pages to Submit</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-mono text-gray-600 bg-white px-3 py-1 rounded border">storajini-indexnow-key</p>
            <p className="text-sm text-gray-500 mt-1">IndexNow Key</p>
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl p-4 mb-6 text-sm text-gray-700">
          <p className="font-semibold mb-2">What gets submitted:</p>
          <ul className="space-y-1 list-disc pl-5">
            <li>Homepage + Compare + About</li>
            <li>All city storage pages</li>
            <li>All city parking pages</li>
            <li>All city moving pages</li>
            <li>Blog listing + all published blog posts</li>
            <li>Legal pages (Privacy, Cookie Policy)</li>
          </ul>
        </div>

        <button
          data-testid="indexnow-submit-btn"
          onClick={submitAll}
          disabled={submitting}
          className="w-full py-4 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2 text-base"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Globe className="w-5 h-5" />}
          {submitting ? "Submitting..." : "Submit All Pages to Bing, Yahoo, Yandex & DuckDuckGo"}
        </button>

        {result && (
          <div className={`mt-4 p-4 rounded-xl text-sm ${result.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {result.success ? `Successfully submitted ${result.urls_submitted || totalPages} URLs!` : result.message}
          </div>
        )}

        <div className="mt-4 p-3 bg-yellow-50 rounded-xl border border-yellow-200 text-sm text-yellow-800">
          <strong>Note:</strong> For Google, you need to manually submit your sitemap at{" "}
          <a href="https://search.google.com/search-console" className="text-amber-600 underline">Google Search Console</a>.
          Your sitemap URL is: <code className="bg-white px-1 py-0.5 rounded text-xs">{window.location.origin}/api/sitemap.xml</code>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "cities", label: "Cities & Scale", icon: MapPin },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "leads", label: "Leads", icon: Users },
  { id: "indexnow", label: "IndexNow", icon: Send },
];

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [activeTab, setActiveTab] = useState("cities");

  if (!auth) return <AdminLogin onLogin={setAuth} />;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <img src="/storajini-logo.png" alt="Storajini" className="w-7 h-7" />
              <span className="font-bold text-sm">Storajini Guide Admin</span>
            </div>
            <button onClick={() => setAuth(null)} className="text-sm text-gray-500 hover:text-gray-700">Logout</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-amber-500 text-amber-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "cities" && <CitiesWaveTab auth={auth} />}
        {activeTab === "blog" && <BlogTab auth={auth} />}
        {activeTab === "leads" && <LeadsTab auth={auth} />}
        {activeTab === "indexnow" && <IndexNowTab auth={auth} />}
      </div>
    </div>
  );
}
